import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { isOptimisticOrder } from "#/hooks/optimistic-updates";
import {
	type BulkClosePosition,
	useCancelAllOrders,
	useCancelOrder,
	useCancelTwap,
	useCloseAllPositions,
	useModifyOrder,
	usePlaceOrder,
} from "#/hooks/use-trading-actions";
import { toNumber } from "#/lib/formatters";
import {
	useTradeAccount,
	useTradeFills,
	useTradeFundingHistory,
	useTradeOpenOrders,
	useTradeOrderHistory,
	useTradePerpMarkets,
	useTradeTwapHistory,
	useTradeTwapSliceFills,
} from "../../../hooks/use-trade-data.ts";
import { getApiCoin } from "../../../lib/hyperliquid.ts";
import {
	absSizeStr,
	computeMarketSlippagePrice,
	MARKET_CLOSE_SLIPPAGE_PCT,
} from "../../../lib/trade-constants.ts";
import { HIDDEN_BALANCE_COINS } from "../constants/positions.constants.ts";
import type {
	ModifyOrderValues,
	OpenOrderRowItem,
	PositionRowItem,
	PositionsTab,
	SideFilter,
} from "../types/positions.types.ts";
import { useEffectiveBalances } from "./use-effective-balances.ts";

type TpslParams = {
	coin: string;
	isLong: boolean;
	tp: string | null;
	sl: string | null;
	size: string;
	markPrice: number;
	useLimitPrice: boolean;
};

export function usePositions() {
	const accountQ = useTradeAccount();
	const openOrdersQ = useTradeOpenOrders();
	const { markets } = useTradePerpMarkets();
	const fillsQ = useTradeFills();
	const fundingQ = useTradeFundingHistory();
	const orderHistoryQ = useTradeOrderHistory();
	const twapHistoryQ = useTradeTwapHistory();
	const twapFillsQ = useTradeTwapSliceFills();
	const balancesResult = useEffectiveBalances();

	const { positions } = accountQ;
	const { openOrders } = openOrdersQ;
	const { fills, fetchNextPage, hasNextPage, isFetchingNextPage } = fillsQ;
	const {
		fundingHistory,
		fetchNextPage: fetchNextFundingPage,
		hasNextPage: hasNextFundingPage,
		isFetchingNextPage: isFetchingNextFundingPage,
	} = fundingQ;
	const { orderHistory } = orderHistoryQ;
	const { twapHistory } = twapHistoryQ;
	const { twapSliceFills } = twapFillsQ;
	const balances = balancesResult.balances;

	const cancelOrder = useCancelOrder();
	const modifyOrder = useModifyOrder();
	const cancelAllOrders = useCancelAllOrders();
	const cancelTwap = useCancelTwap();
	const placeOrder = usePlaceOrder();
	const closeAllPositions = useCloseAllPositions();

	const navigate = useNavigate();
	const [tab, setTab] = useState<PositionsTab>("positions");
	const [sideFilter, setSideFilter] = useState<SideFilter>("all");
	const [hideSmallBalances, setHideSmallBalances] = useState(false);

	const markPriceMap = useMemo(() => {
		const map: Record<string, number> = {};
		for (const m of markets) map[m.coin] = m.markPrice;
		return map;
	}, [markets]);

	const szDecimalsByCoin = useMemo(() => {
		const map: Record<string, number> = {};
		for (const market of markets) map[market.coin] = market.szDecimals;
		return map;
	}, [markets]);

	const enrichedPositions: PositionRowItem[] = useMemo(
		() =>
			positions.map((p) => ({
				...p,
				markPx: markPriceMap[p.coin] ?? null,
			})),
		[positions, markPriceMap],
	);

	const openPositions = useMemo(
		() => enrichedPositions.filter((position) => toNumber(position.szi) !== 0),
		[enrichedPositions],
	);

	const filteredPositions = useMemo(() => {
		if (sideFilter === "all") return enrichedPositions;
		return enrichedPositions.filter((p) => {
			const szi = Number(p.szi ?? 0);
			return sideFilter === "long" ? szi > 0 : szi < 0;
		});
	}, [enrichedPositions, sideFilter]);

	const filteredOpenOrders = useMemo(() => {
		if (sideFilter === "all") return openOrders;
		return openOrders.filter((o) =>
			sideFilter === "long" ? o.side === "B" : o.side === "A",
		);
	}, [openOrders, sideFilter]);

	const filteredFills = useMemo(() => {
		if (sideFilter === "all") return fills;
		return fills.filter((f) =>
			sideFilter === "long" ? f.side === "B" : f.side === "A",
		);
	}, [fills, sideFilter]);

	const filteredFundingHistory = useMemo(() => {
		if (sideFilter === "all") return fundingHistory;
		return fundingHistory.filter((f) => {
			const szi = Number(f.delta.szi);
			return sideFilter === "long" ? szi > 0 : szi < 0;
		});
	}, [fundingHistory, sideFilter]);

	const filteredOrderHistory = useMemo(() => {
		if (sideFilter === "all") return orderHistory;
		return orderHistory.filter((o) =>
			sideFilter === "long" ? o.order.side === "B" : o.order.side === "A",
		);
	}, [orderHistory, sideFilter]);

	const filteredTwapHistory = useMemo(() => {
		if (sideFilter === "all") return twapHistory;
		return twapHistory.filter((t) =>
			sideFilter === "long" ? t.state.side === "B" : t.state.side === "A",
		);
	}, [twapHistory, sideFilter]);

	const visibleBalanceCount = useMemo(
		() =>
			balances.filter((balance) => !HIDDEN_BALANCE_COINS.has(balance.coin))
				.length,
		[balances],
	);

	function handleCloseMarket(coin: string, size: string, isLong: boolean) {
		const mark = markPriceMap[coin];
		if (!mark) return;
		placeOrder.mutate({
			coin,
			side: isLong ? "sell" : "buy",
			orderType: "Market order",
			price: computeMarketSlippagePrice(
				mark,
				isLong,
				MARKET_CLOSE_SLIPPAGE_PCT,
			),
			size,
			reduceOnly: true,
			timeInForce: "FrontendMarket",
			triggerPrice: "0",
			markPrice: mark,
		});
	}

	function handleCloseLimitSubmit(
		coin: string,
		size: string,
		isLong: boolean,
		price: string,
	) {
		const mark = markPriceMap[coin];
		if (!mark) return;
		placeOrder.mutate({
			coin,
			side: isLong ? "sell" : "buy",
			orderType: "Limit order",
			price,
			size,
			reduceOnly: true,
			timeInForce: "Gtc",
			triggerPrice: "0",
			markPrice: mark,
		});
	}

	function handleReverse(coin: string, size: string, isLong: boolean) {
		const mark = markPriceMap[coin];
		if (!mark) return;
		const doubleSize = Number((Number(size) * 2).toPrecision(6)).toString();
		placeOrder.mutate({
			coin,
			side: isLong ? "sell" : "buy",
			orderType: "Market order",
			price: computeMarketSlippagePrice(
				mark,
				isLong,
				MARKET_CLOSE_SLIPPAGE_PCT,
			),
			size: doubleSize,
			reduceOnly: false,
			timeInForce: "FrontendMarket",
			triggerPrice: "0",
			markPrice: mark,
		});
	}

	function handleCloseAll() {
		const payload: BulkClosePosition[] = [];
		for (const p of openPositions) {
			const mark = markPriceMap[p.coin];
			if (!mark) continue;
			const szi = toNumber(p.szi);
			payload.push({
				coin: p.coin,
				size: absSizeStr(p.szi),
				isLong: szi >= 0,
				markPrice: mark,
			});
		}
		if (payload.length > 0) closeAllPositions.mutate(payload);
	}

	function handleSetTpsl(params: TpslParams) {
		const {
			coin,
			isLong,
			tp,
			sl,
			size,
			markPrice: mark,
			useLimitPrice,
		} = params;
		const side: "buy" | "sell" = isLong ? "sell" : "buy";

		if (tp) {
			placeOrder.mutate({
				coin,
				side,
				orderType: useLimitPrice ? "Take profit limit" : "Take profit market",
				price: tp,
				size,
				reduceOnly: true,
				timeInForce: "Gtc",
				triggerPrice: tp,
				markPrice: mark,
			});
		}
		if (sl) {
			placeOrder.mutate({
				coin,
				side,
				orderType: useLimitPrice ? "Stop limit" : "Stop market",
				price: sl,
				size,
				reduceOnly: true,
				timeInForce: "Gtc",
				triggerPrice: sl,
				markPrice: mark,
			});
		}
	}

	function handleCancelOrder(coin: string, oid: number) {
		// Optimistic placeholders (negative oid) aren't cancellable — no real oid yet.
		if (isOptimisticOrder({ oid })) return;
		cancelOrder.mutate({ coin, oid });
	}

	function handleModifyOrder(
		order: OpenOrderRowItem,
		values: ModifyOrderValues,
	) {
		// Same guard as cancel — a placeholder has no real oid to modify.
		if (isOptimisticOrder(order)) return;
		modifyOrder.mutate({ order, ...values });
	}

	function handleCancelAllOrders() {
		const cancellable = openOrders.filter((order) => !isOptimisticOrder(order));
		if (cancellable.length === 0) return;
		cancelAllOrders.mutate(
			cancellable.map((order) => ({ coin: order.coin, oid: order.oid })),
		);
	}

	function handleNavigate(coin: string) {
		navigate({ to: "/trade/$symbol", params: { symbol: getApiCoin(coin) } });
	}

	return {
		tabs: {
			tab,
			positionCount: enrichedPositions.length,
			openOrderCount: openOrders.length,
			balanceCount: visibleBalanceCount,
			sideFilter,
			hideSmallBalances,
			onTabChange: setTab,
			onSideFilterChange: setSideFilter,
			onHideSmallBalancesChange: setHideSmallBalances,
		},
		data: {
			balances,
			hideSmallBalances,
			openOrders,
			filteredPositions,
			filteredOpenOrders,
			filteredFills,
			filteredFundingHistory,
			filteredOrderHistory,
			filteredTwapHistory,
			twapSliceFills,
			szDecimalsByCoin,
			fetchNextPage,
			hasNextPage: hasNextPage ?? false,
			isFetchingNextPage,
			fetchNextFundingPage,
			hasNextFundingPage: hasNextFundingPage ?? false,
			isFetchingNextFundingPage,
			errors: {
				positions: {
					isError: accountQ.isError,
					onRetry: () => {
						accountQ.refetch();
					},
				},
				openOrders: {
					isError: openOrdersQ.isError,
					onRetry: () => {
						openOrdersQ.refetch();
					},
				},
				fills: {
					isError: fillsQ.isError,
					onRetry: () => {
						fillsQ.refetch();
					},
				},
				fundingHistory: {
					isError: fundingQ.isError,
					onRetry: () => {
						fundingQ.refetch();
					},
				},
				orderHistory: {
					isError: orderHistoryQ.isError,
					onRetry: () => {
						orderHistoryQ.refetch();
					},
				},
				twap: {
					isError: twapHistoryQ.isError || twapFillsQ.isError,
					onRetry: () => {
						twapHistoryQ.refetch();
						twapFillsQ.refetch();
					},
				},
				balances: {
					isError: balancesResult.isError,
					onRetry: balancesResult.refetch,
				},
			},
		},
		actions: {
			onNavigate: handleNavigate,
			onCloseMarket: handleCloseMarket,
			onCloseLimitSubmit: handleCloseLimitSubmit,
			onReverse: handleReverse,
			onCloseAll: handleCloseAll,
			onSetTpsl: handleSetTpsl,
			onCancelOrder: handleCancelOrder,
			onCancelAllOrders: handleCancelAllOrders,
			onCancelTwap: (coin: string, twapId: number) =>
				cancelTwap.mutate({ coin, twapId }),
			onCancelOrderFromOpenOrders: (order: { coin: string; oid: number }) =>
				handleCancelOrder(order.coin, order.oid),
			onModifyOrder: handleModifyOrder,
		},
	};
}

export type PositionsState = ReturnType<typeof usePositions>;
